import { useEffect, useState } from 'react'
import {
  ActionIcon,
  Badge,
  Button,
  Card,
  Center,
  Group,
  Loader,
  Menu,
  Modal,
  Stack,
  Switch,
  Text,
  TextInput,
  Textarea,
} from '@mantine/core'
import {
  IconChecklist,
  IconDotsVertical,
  IconPencil,
  IconPlus,
  IconTrash,
} from '@tabler/icons-react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { notifications } from '@mantine/notifications'
import type { ProjectTemplateDto } from '@ledokol/contracts'
import {
  createTemplate,
  deleteTemplate,
  fetchTemplates,
  updateTemplate,
} from '../../shared/api/templates'
import { useAuthStore } from '../auth/store'
import { isApiError } from '../../shared/api/client'
import { TemplateDetailModal } from './template-detail-modal'

export function TemplatesTab() {
  const { user } = useAuthStore()
  const isAdmin = user?.role === 'admin'
  const qc = useQueryClient()

  const listQ = useQuery({ queryKey: ['templates'], queryFn: fetchTemplates })

  const [formOpen, setFormOpen] = useState(false)
  const [editing, setEditing] = useState<ProjectTemplateDto | null>(null)
  const [detailId, setDetailId] = useState<string | null>(null)
  const [deleteTarget, setDeleteTarget] = useState<ProjectTemplateDto | null>(null)

  const remove = useMutation({
    mutationFn: (id: string) => deleteTemplate(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['templates'] })
      notifications.show({ color: 'teal', title: 'Удалено', message: 'Шаблон удалён' })
      setDeleteTarget(null)
    },
    onError: () => notifications.show({ color: 'red', title: 'Ошибка', message: 'Не удалось удалить' }),
  })

  return (
    <Stack gap="md">
      <Group justify="space-between">
        <Text c="dimmed" size="sm">
          Шаблоны разворачиваются в задачи при создании проекта «Новый клиент»
        </Text>
        {isAdmin && (
          <Button
            leftSection={<IconPlus size={16} />}
            onClick={() => {
              setEditing(null)
              setFormOpen(true)
            }}
          >
            Новый шаблон
          </Button>
        )}
      </Group>

      {listQ.isLoading && (
        <Center mih={160}>
          <Loader />
        </Center>
      )}

      {listQ.data && listQ.data.length === 0 && (
        <Card withBorder p="xl" radius="md">
          <Center>
            <Stack align="center" gap="xs">
              <IconChecklist size={40} color="var(--mantine-color-gray-5)" />
              <Text c="dimmed" size="sm">
                Шаблонов пока нет
              </Text>
            </Stack>
          </Center>
        </Card>
      )}

      {listQ.data?.map((t) => (
        <Card key={t.id} withBorder p="md" radius="md">
          <Group justify="space-between" wrap="nowrap" align="flex-start">
            <Stack gap={4} style={{ minWidth: 0 }}>
              <Group gap="xs">
                <Text fw={600}>{t.name}</Text>
                <Badge color={t.is_active ? 'teal' : 'gray'} variant="light" size="sm">
                  {t.is_active ? 'Активен' : 'Выключен'}
                </Badge>
                <Badge color="blue" variant="light" size="sm">
                  {t.tasks.length} задач
                </Badge>
              </Group>
              {t.description && (
                <Text size="sm" c="dimmed">
                  {t.description}
                </Text>
              )}
            </Stack>

            <Group gap="xs" wrap="nowrap">
              <Button size="xs" variant="light" onClick={() => setDetailId(t.id)}>
                {isAdmin ? 'Открыть' : 'Посмотреть'}
              </Button>
              {isAdmin && (
                <Menu position="bottom-end" shadow="md">
                  <Menu.Target>
                    <ActionIcon variant="subtle" color="gray">
                      <IconDotsVertical size={18} />
                    </ActionIcon>
                  </Menu.Target>
                  <Menu.Dropdown>
                    <Menu.Item
                      leftSection={<IconPencil size={16} />}
                      onClick={() => {
                        setEditing(t)
                        setFormOpen(true)
                      }}
                    >
                      Переименовать
                    </Menu.Item>
                    <Menu.Item leftSection={<IconTrash size={16} />} color="red" onClick={() => setDeleteTarget(t)}>
                      Удалить
                    </Menu.Item>
                  </Menu.Dropdown>
                </Menu>
              )}
            </Group>
          </Group>
        </Card>
      ))}

      <TemplateFormModal opened={formOpen} onClose={() => setFormOpen(false)} editing={editing} />

      <TemplateDetailModal
        templateId={detailId}
        opened={!!detailId}
        onClose={() => setDetailId(null)}
        isAdmin={isAdmin}
      />

      <Modal
        opened={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        title={<Text fw={700}>Удалить шаблон?</Text>}
        size="sm"
      >
        <Stack gap="md">
          <Text size="sm">
            «{deleteTarget?.name}» и все его пункты ({deleteTarget?.tasks.length}) будут удалены. Уже
            созданные по нему задачи проектов не затрагиваются.
          </Text>
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setDeleteTarget(null)}>
              Отмена
            </Button>
            <Button color="red" loading={remove.isPending} onClick={() => deleteTarget && remove.mutate(deleteTarget.id)}>
              Удалить
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  )
}

function TemplateFormModal({
  opened,
  onClose,
  editing,
}: {
  opened: boolean
  onClose: () => void
  editing: ProjectTemplateDto | null
}) {
  const qc = useQueryClient()
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [isActive, setIsActive] = useState(true)

  useEffect(() => {
    if (!opened) return
    setName(editing?.name ?? '')
    setDescription(editing?.description ?? '')
    setIsActive(editing?.is_active ?? true)
  }, [opened, editing])

  const save = useMutation({
    mutationFn: () => {
      const body = { name: name.trim(), description: description.trim() || null, is_active: isActive }
      return editing ? updateTemplate(editing.id, body) : createTemplate(body)
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['templates'] })
      notifications.show({ color: 'teal', title: editing ? 'Шаблон обновлён' : 'Шаблон создан', message: name.trim() })
      onClose()
    },
    onError: (e) => {
      const msg = isApiError(e)
        ? e.response?.status === 409
          ? 'Шаблон с таким именем уже есть'
          : (e.response?.data.error.message ?? 'Не удалось сохранить')
        : 'Не удалось сохранить'
      notifications.show({ color: 'red', title: 'Ошибка', message: msg })
    },
  })

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      size="md"
      title={<Text fw={700}>{editing ? 'Редактировать шаблон' : 'Новый шаблон'}</Text>}
    >
      <Stack gap="md">
        <TextInput
          label="Название"
          required
          placeholder="Например: Онбординг нового клиента"
          value={name}
          onChange={(e) => setName(e.currentTarget.value)}
        />
        <Textarea
          label="Описание (необязательно)"
          autosize
          minRows={2}
          value={description}
          onChange={(e) => setDescription(e.currentTarget.value)}
        />
        <Switch
          label="Активен"
          description="Только активные шаблоны можно выбрать при создании проекта"
          checked={isActive}
          onChange={(e) => setIsActive(e.currentTarget.checked)}
        />
        <Group justify="flex-end" mt="xs">
          <Button variant="default" onClick={onClose}>
            Отмена
          </Button>
          <Button onClick={() => save.mutate()} loading={save.isPending} disabled={name.trim().length === 0}>
            {editing ? 'Сохранить' : 'Создать'}
          </Button>
        </Group>
      </Stack>
    </Modal>
  )
}
